import React from 'react';
import {
  DashboardOutlined,
  HomeOutlined,
  LogoutOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { Link, Outlet, useNavigate, useParams } from 'react-router';
import { Avatar, Dropdown, Layout, Menu, Modal } from 'antd';
import type { UnionExpand } from '@owl-lib/type-util';
import OwlLogo from '../../assets/logo-28.svg';
import { Locale, SessionContext } from '../../context/AppProvider';
import { FeatureKeys } from '../../data/session/interface';
import { actions as sessionSliceActions } from '../../data/session/logic';
import type { Params } from '../ClaimOverviewContainer/ClaimOverviewContainer';
import { ShellRouteDefs } from '../ClaimOverviewShellContainer/ClaimOverviewLandingContainer';
import { GeneralFeedbackComponent } from '../FeedbackContainer/FeedbackComponents';
import NotificationsContainer from '../NotificationsContainer/NotificationsContainer';
import { generatePath, RouteDefs } from '../Routing/Routing';
import styles from './MainContainer.module.scss';

export const UserContextMenuComponent: React.FC = () => {
  const { dossierId } = useParams() as UnionExpand<Params>;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const handleOk = () => {
    dispatch(sessionSliceActions.requestLogout());
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };
  const session = React.useContext(SessionContext);
  const { messages } = React.useContext(Locale);
  const dropdownItems = React.useMemo(() => {
    return [
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: (
          <Link
            data-testid="logout-btn"
            to="#"
            onClick={() => setIsModalOpen(true)}
          >
            {messages['platform.logout']}
          </Link>
        ),
      },
    ];
  }, [messages]);

  return (
    <div className={styles.userContextMenuContainer}>
      <GeneralFeedbackComponent dossier_id={dossierId} />
      <NotificationsContainer />
      <Avatar
        className={styles.statusAvatar}
        shape="circle"
        size={28}
        icon={<DashboardOutlined style={{ fontSize: '14px' }} />}
        onClick={() => navigate(generatePath(RouteDefs.Status))}
      />

      <Dropdown
        placement="topRight"
        menu={{ selectable: false, items: dropdownItems }}
        trigger={['click']}
      >
        <Avatar
          className={styles.avatar}
          icon={
            session?.current?.first_name ||
            session?.current?.last_name ? undefined : (
              <UserOutlined style={{ fontSize: '14px' }} />
            )
          }
          size={28}
        >
          <div style={{ fontSize: '8px', fontWeight: 700 }}>
            {session?.current
              ? `${session.current.first_name?.[0] ?? ''}${
                  session.current.last_name?.[0] ?? ''
                }`.toUpperCase()
              : null}
          </div>
        </Avatar>
      </Dropdown>
      <Modal open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
        <p>{messages['platform.logout_description']}</p>
      </Modal>
    </div>
  );
};

const MainContainer = () => {
  const navigate = useNavigate();
  const { messages } = React.useContext(Locale);
  const { current, features } = React.useContext(SessionContext);

  React.useEffect(() => {
    if (features[FeatureKeys.DemoClaimOverview]) {
      navigate(generatePath(ShellRouteDefs.OwlShell));
    }
  }, [features, navigate]);

  return (
    <Layout className={styles.layout}>
      <Layout.Header className={styles.header}>
        <OwlLogo />
        {features[FeatureKeys.AdminPage] && (
          <Menu
            className={styles.menu}
            selectedKeys={[location.pathname.split('/')[1]]}
            mode="horizontal"
            items={[
              {
                key: 'claims',
                icon: <HomeOutlined />,
                label: (
                  <Link to={generatePath(RouteDefs.Claims)}>
                    {features[FeatureKeys.CustomerTerminology]
                      ? messages['claims.title']
                      : messages['claims.cases']}
                  </Link>
                ),
              },
              ...(current?.has_manager_role
                ? [
                    {
                      key: 'admin',
                      icon: <UserSwitchOutlined />,
                      label: (
                        <Link to={generatePath(RouteDefs.AdminUsers)}>
                          {messages['admin.title']}
                        </Link>
                      ),
                    },
                  ]
                : []),
            ]}
            selectable
          />
        )}
        <UserContextMenuComponent />
      </Layout.Header>
      <Layout className={styles.content}>
        <Outlet />
      </Layout>
    </Layout>
  );
};

export default MainContainer;
